import { defineStore } from 'pinia'
import { computed, type ComputedRef, ref } from 'vue'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import School, { SchoolInput, SchoolMe } from '@/core/domain/school/core/School.ts'
import FetchAllSchoolsUseCase from '@/core/useCases/school/core/FetchAllSchoolsUseCase.ts'
import FetchSchoolsForMeUseCase from '@/core/useCases/school/core/FetchSchoolsForMeUseCase.ts'
import FetchSchoolDirectoryEntryUseCase from '@/core/useCases/school/core/FetchSchoolDirectoryEntryUseCase.ts'
import CreateSchoolUseCase from '@/core/useCases/school/core/CreateSchoolUseCase.ts'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import { UpdateSchoolUseCase } from '@/core/useCases/school/core/UpdateSchoolUseCase.ts'
import { emitStoreEvent } from '@/presentation/stores/utils/storeEventBus.ts'

export const useSchoolStore = defineStore('School', () => {
  // BASE COMMUNE
  const baseStore = createStoreBase([
    'fetchAll',
    'fetchForMe',
    'fetchFromDataEducation',
    'create',
    'update',
    'delete'
  ])

  // ÉTAT
  const schoolList = ref<SchoolMe[]>([])
  const allSchools = ref<School[]>([])
  const selectedSchool = ref<School | null>(null)

  const schoolsBySlug = ref<Record<string, School>>({})

  // ACTIONS PUBLIQUES
  /**
   * Récupère tous les établissements (pour l'administration)
   */
  async function fetchAllSchools(): Promise<School[]> {
    try {
      baseStore.loading.value.fetchAll = true
      baseStore.resetError()

      const schools = await DependencyContainer.resolve(FetchAllSchoolsUseCase).execute()
      allSchools.value = schools
      baseStore.lastUpdated.value = new Date()

      for (const school of schools) {
        schoolsBySlug.value[school.slug] = school
      }

      return schools
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.fetchAll = false
    }
  }

  /**
   * Récupère les établissements pour l'utilisateur actuel
   */
  async function fetchSchoolsForMe(): Promise<SchoolMe[]> {
    try {
      baseStore.loading.value.fetchForMe = true
      baseStore.resetError()

      const schools = await DependencyContainer.resolve(FetchSchoolsForMeUseCase).execute()
      schoolList.value = schools
      baseStore.lastUpdated.value = new Date()

      return schools
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.fetchForMe = false
    }
  }

  /**
   * Récupère les données d'un établissement depuis l'API de l'éducation nationale
   * @param codeUai Code UAI de l'établissement
   */
  async function fetchSchoolFromDataEducation(codeUai: string): Promise<SchoolInput> {
    try {
      baseStore.loading.value.fetchFromDataEducation = true
      baseStore.resetError()

      const result = await DependencyContainer.resolve(FetchSchoolDirectoryEntryUseCase).execute(
        codeUai
      )

      if (result && result.codeUai !== '') {
        baseStore.showSuccessToast(
          'Établissement trouvé',
          "L'établissement a bien été trouvé dans la base de données de l'éducation nationale."
        )
      } else {
        baseStore.showErrorToast(
          'Établissement introuvable',
          'Impossible de récupérer les informations de cet établissement'
        )
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.fetchFromDataEducation = false
    }
  }

  /**
   * Crée un nouvel établissement
   * @param school Données de l'établissement à créer
   * @return L'établissement créé
   */
  async function createSchool(school: SchoolInput): Promise<School> {
    try {
      baseStore.loading.value.create = true
      baseStore.resetError()

      const newSchool = await DependencyContainer.resolve(CreateSchoolUseCase).execute(school)

      if (newSchool) {
        allSchools.value.push(newSchool)
        baseStore.lastUpdated.value = new Date()

        schoolsBySlug.value[newSchool.slug] = newSchool

        baseStore.showSuccessToast('Établissement créé', "L'établissement a bien été créé.")
      }

      return newSchool
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.create = false
    }
  }

  /**
   * Met à jour un établissement existant
   * @param id Identifiant de l'établissement à mettre à jour
   * @param school Données de l'établissement à mettre à jour
   */
  async function updateSchool(id: number, school: SchoolInput): Promise<School> {
    try {
      baseStore.loading.value.update = true
      baseStore.resetError()

      // Supposons que vous avez un cas d'utilisation UpdateSchoolUseCase
      const updatedSchool = await DependencyContainer.resolve(UpdateSchoolUseCase).execute(
        id,
        school
      )

      if (updatedSchool) {
        // Mettre à jour l'établissement dans les listes
        const allIndex = allSchools.value.findIndex((s) => s.id === id)
        if (allIndex !== -1) {
          allSchools.value[allIndex] = updatedSchool
        }

        const meIndex = schoolList.value.findIndex((s) => s.id === id)
        if (meIndex !== -1 && 'classrooms' in updatedSchool) {
          schoolList.value[meIndex] = updatedSchool as SchoolMe
        }

        schoolsBySlug.value[updatedSchool.slug] = updatedSchool

        baseStore.lastUpdated.value = new Date()

        baseStore.showSuccessToast(
          'Établissement mis à jour',
          "L'établissement a bien été mis à jour."
        )
      }

      return updatedSchool
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.update = false
    }
  }

  /**
   * Sélectionne un établissement comme établissement courant
   * @param school Établissement à sélectionner
   */
  function selectSchool(school: School | null): void {
    selectedSchool.value = school

    // Émettre un événement pour notifier tous les stores dépendants
    if (school) {
      emitStoreEvent('school:changed', { schoolId: school.id })
    }
  }

  /**
   * Réinitialise le store
   */
  function reset(): void {
    schoolList.value = []
    allSchools.value = []
    selectedSchool.value = null
    baseStore.resetError()

    baseStore.loading.value = {
      fetchAll: false,
      fetchForMe: false,
      fetchFromDataEducation: false,
      create: false,
      update: false,
      delete: false
    }
  }

  // GETTERS (COMPUTED)
  const getSchoolById: ComputedRef<(id: number) => SchoolMe | undefined> = computed(() => {
    return (id: number) => schoolList.value.find((school) => school.id === id)
  })

  const getSchoolBySlug: ComputedRef<(slug: string) => School | undefined> = computed(() => {
    return (slug: string) => schoolsBySlug.value[slug]
  })

  const getSchoolByCodeUai: ComputedRef<(codeUai: string) => SchoolMe | undefined> = computed(
    () => {
      return (codeUai: string) => schoolList.value.find((school) => school.codeUai === codeUai)
    }
  )

  const isLoading: ComputedRef<boolean> = computed(() =>
    Object.values(baseStore.loading.value).some(Boolean)
  )

  const schoolCount: ComputedRef<number> = computed(() => schoolList.value.length)

  const allSchoolCount: ComputedRef<number> = computed(() => allSchools.value.length)

  return {
    // État
    schoolList,
    allSchools,
    selectedSchool,
    schoolStoreError: baseStore.error,
    schoolStoreLoading: baseStore.loading,
    schoolStoreLastUpdated: baseStore.lastUpdated,

    // Actions
    fetchSchoolsForMe,
    fetchAllSchools,
    fetchSchoolFromDataEducation,
    createSchool,
    updateSchool,
    selectSchool,
    reset,

    // Getters
    getSchoolById,
    getSchoolBySlug,
    getSchoolByCodeUai,
    schoolStoreIsLoading: isLoading,
    schoolCount,
    allSchoolCount
  }
})
